import { Modal, Form, Input, Button, Select } from "antd";
import { useRef, useState, useEffect } from "react";
import Draggable from "react-draggable";
import { useSelector } from "react-redux";

import { formItemLayout, inputLayout } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import Loading from "../../../common/Loading";
import FormInputRules from "../../../../util/FormInputRules";
import { General } from "../../../../util/General";
import { ModuleAndIdMapping, Modules } from "../../../../constants/DatabaseConstants/MasterConstant";
import useGetOneMasterList from "../../../../hooks/useGetOneMasterList";

import styles from "../../index.module.less";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import type { RootState } from "../../../../appRedux/store";

type PricingMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

const PricingMaster = (props: PricingMasterProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);

  /* STATES */
  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);

  /* CUSTOM HOOKS */
  const { loading: vehicleTypeListLoading, masterList: vehicleTypeMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.VEHICLE_TYPE_MASTER]);

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  const { bounds, onStart } = useReactDraggable();

  /* HANDLERS */

  const handleFinish = (data: any) => {
    if (props.addRecord) {
      props.handleSubmit({ ...data, customer_id: masterFilters?.customerId, customer_location_id: masterFilters?.locationId });
    } else {
      props.handleSubmit({ ...data, customer_id: props.rowData?.customer_id, customer_location_id: props.rowData?.customer_location_id });
    }
  };

  /* loading states from multiple hooks added as expression */
  const isOnAddLoading = !(props.addRecord && !vehicleTypeListLoading);
  const isOnEditLoading = !(!props.addRecord && !vehicleTypeListLoading);

  /* EFFECTS */
  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      form.setFieldsValue(props.rowData);
      // setCustomerId(props.rowData?.customer_id);
      setFormFieldsLoadedToForm(true);
    }
    // eslint-disable-next-line
  }, [isOnEditLoading]);

  return (
    <Modal
      afterClose={() => form.resetFields()}
      destroyOnClose={true}
      maskClosable={false}
      centered
      title={<div className="modal-container">{`${props.addRecord ? "Add" : "Edit"} Pricing Master`}</div>}
      open={true}
      onCancel={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}
      width={"50%"}
      okButtonProps={{
        style: {
          display: "none"
        }
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
    >
      {((props.addRecord && isOnAddLoading) || (!props.addRecord && !formFieldsLoadedToForm)) && (
        <div className={`${styles["textCenter"]}`}>
          <Loading />
        </div>
      )}

      {((props.addRecord && !isOnAddLoading) || (!props.addRecord && formFieldsLoadedToForm)) && (
        <Form
          ref={formRef}
          onValuesChange={(changedValues) => {
            const fieldName = Object.keys(changedValues)[0];
            form.validateFields([fieldName]);
          }}
          className={`${styles["formDiv"]}`}
          {...formItemLayout}
          form={form}
          validateTrigger="onBlur"
          colon={false}
          name="register"
          onFinish={handleFinish}
        >
          <Form.Item {...inputLayout} name="vehicle_type_id" label="Vehicle Type" rules={[FormInputRules.requiredFieldSelectRule("vehicle type name")]}>
            <Select className={`${styles["from-Item-selectDiv"]}`} showSearch allowClear getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select vehicle type">
              {vehicleTypeMasterList?.map((item) => (
                <Select.Option key={item.id} value={item.id} className={`${styles["ant-select-option"]}`}>
                  {item.vehicle_type_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item {...inputLayout} name="price_per_hour" label="Price Per Hour" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("Price/Hour"), FormInputRules.NumberValidationRule()]}>
            <Input name="price_per_hour" />
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} htmlType="submit">
              {props.saving ? <CircularProgressBar /> : props.addRecord ? "Submit" : "Update"}
            </Button>
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button disabled={props.saving} className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} onClick={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}>
              Close
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default PricingMaster;
